import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Coins, Clock, Loader2, Users, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Loot Auctions — public list of active/scheduled/finalized auctions.
 * Hunters bid Loot Coins. Top bidder wins, losers get 50% of their highest
 * bid back, the rest goes to the Community Tank.
 */
export default function AuctionsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);

  const reload = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/auctions");
      setItems(data?.items || []);
    } catch {
      setItems([]);
    } finally { setBusy(false); }
  };
  useEffect(() => { reload(); }, []);

  const live = items.filter((i) => i.status === "active");
  const finalized = items.filter((i) => i.status === "finalized").slice(0, 4);

  return (
    <div className="py-10 space-y-8" data-testid="auctions-page">
      <header className="max-w-3xl mx-auto text-center">
        <div className="overline" style={{ color: "#D7A62A" }}>weekly bid wars</div>
        <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2 leading-[0.9]">
          Loot
          <span style={{ color: "#76B82A" }}> Auctions</span>.
        </h1>
        <p className="mt-3 text-base text-[#F5F2E8]/75 max-w-xl mx-auto">
          One real prize on the block. Bid your Loot Coins. Top bidder wins, losers get half back, the rest funds the Community Tank.
        </p>
      </header>

      {busy && items.length === 0 && (
        <div className="text-center"><Loader2 size={28} className="inline animate-spin text-[#D7A62A]" /></div>
      )}

      {live.length === 0 && !busy && (
        <div className="max-w-2xl mx-auto loot-card-flat p-8 text-center" data-testid="auctions-empty">
          <Trophy size={32} className="inline text-[#D7A62A] mb-2" />
          <div className="font-display uppercase text-xl">No live auctions right now</div>
          <p className="font-mono text-xs text-[#F5F2E8]/55 mt-2 max-w-md mx-auto">
            Check back soon — we run them weekly. Stack your <Link to="/bazaar" className="underline">Loot Coins</Link> in the meantime.
          </p>
        </div>
      )}

      <div className="max-w-3xl mx-auto space-y-3" data-testid="auctions-live-list">
        {live.map((a) => (
          <AuctionCard key={a.id} auction={a} onBid={() => setActive(a)} username={user?.username} />
        ))}
      </div>

      {finalized.length > 0 && (
        <div className="max-w-3xl mx-auto">
          <div className="overline mb-2">Recent finalized</div>
          <div className="space-y-2" data-testid="auctions-finalized-list">
            {finalized.map((a) => (
              <div key={a.id} className="loot-card-flat p-3 flex items-center gap-3 flex-wrap" data-testid={`auction-finalized-${a.id}`}>
                {a.prize_image_url && <img src={a.prize_image_url} alt="" className="h-10 w-10 object-contain border border-[#F5F2E8]/15" />}
                <div className="flex-1 min-w-[200px]">
                  <div className="font-display uppercase text-sm">{a.prize_label}</div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/55">
                    Won by <strong>@{a.winner_username || "—"}</strong> with {a.winning_amount}c
                  </div>
                </div>
                <div className="font-display text-sm" style={{ color: "#76B82A" }}>${(a.prize_value_cents / 100).toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {active && (
        <BidModal
          auction={active}
          onClose={() => setActive(null)}
          onBidPlaced={() => { setActive(null); reload(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function AuctionCard({ auction, onBid, username }) {
  const a = auction;
  const dollars = (a.prize_value_cents / 100).toFixed(2);
  const isMine = username && a.top_bidder === username;
  const minNext = Math.max(a.min_bid, (a.top_bid || 0) + a.bid_increment);
  return (
    <div className="loot-card-flat p-5 space-y-3" data-testid={`auction-card-${a.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        {a.prize_image_url && (
          <img src={a.prize_image_url} alt="" className="h-20 w-20 object-contain border border-[#F5F2E8]/15" />
        )}
        <div className="flex-1 min-w-[200px]">
          <div className="font-display uppercase text-2xl leading-tight">{a.prize_label}</div>
          <div className="font-mono text-[11px] text-[#F5F2E8]/65 mt-1">${dollars} retail value</div>
          {a.description && <p className="text-xs text-[#F5F2E8]/75 mt-2">{a.description}</p>}
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">Top bid</div>
          <div className="font-display text-3xl" style={{ color: "#D7A62A" }}>
            {a.top_bid || 0}<span className="text-sm">c</span>
          </div>
          {a.top_bidder && (
            <div className="font-mono text-[10px] text-[#F5F2E8]/55">@{a.top_bidder}{isMine && <span style={{color:"#76B82A"}}> (you)</span>}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs text-[#F5F2E8]/65 flex-wrap">
        <span className="inline-flex items-center gap-1"><Users size={12} /> {a.bidder_count} bidder{a.bidder_count === 1 ? "" : "s"}</span>
        <span className="inline-flex items-center gap-1"><Coins size={12} /> {a.bid_count} bids placed</span>
        <span className="inline-flex items-center gap-1"><Clock size={12} /> ends {a.ends_at?.slice(0, 16).replace("T", " ")}</span>
      </div>
      {a.my_bid && (
        <div className="font-mono text-[10px] text-[#76B82A]">Your highest bid: {a.my_bid.amount}c{isMine ? " — leading!" : ""}</div>
      )}
      <button
        onClick={onBid}
        className="btn-loot w-full text-xs inline-flex items-center justify-center gap-1"
        data-testid={`auction-bid-${a.id}`}
      >
        <ArrowUpRight size={12} /> Bid (min {minNext}c)
      </button>
    </div>
  );
}

function BidModal({ auction, onClose, onBidPlaced }) {
  const minNext = Math.max(auction.min_bid, (auction.top_bid || 0) + auction.bid_increment);
  const [amount, setAmount] = useState(minNext);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (amount < minNext) return toast.error(`Min next bid is ${minNext}c`);
    setBusy(true);
    try {
      const { data } = await api.post(`/auctions/${auction.id}/bid`, { amount });
      toast.success(`Bid placed at ${data.amount}c. Balance: ${data.balance}.`);
      onBidPlaced();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bid failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-end md:items-center justify-center p-4"
      onClick={onClose}
      data-testid="bid-modal"
    >
      <div className="loot-card-flat max-w-md w-full p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="overline" style={{ color: "#D7A62A" }}>place a bid</div>
            <div className="font-display uppercase text-xl mt-1">{auction.prize_label}</div>
          </div>
          <button onClick={onClose} className="btn-ghost text-xs">Close</button>
        </div>
        <p className="font-mono text-xs text-[#F5F2E8]/65">
          Current top: <strong>{auction.top_bid || 0}c</strong>. Bid <strong>{minNext}c</strong> or more. Coins are locked immediately. If you don't win, you'll get 50% back automatically when the auction finalizes.
        </p>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Your bid</label>
          <input
            type="number"
            min={minNext}
            step={auction.bid_increment}
            value={amount}
            onChange={(e) => setAmount(Math.max(minNext, parseInt(e.target.value || minNext, 10)))}
            className="loot-input"
            data-testid="bid-amount"
          />
        </div>
        <button onClick={submit} disabled={busy} className="btn-loot w-full text-xs" data-testid="bid-submit">
          {busy ? <Loader2 size={12} className="inline animate-spin mr-1" /> : null}
          Lock {amount}c bid
        </button>
      </div>
    </div>
  );
}
